import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"
import {
  segmentedCount,
  segmentedItem,
  segmentedItemActivoFicha,
  segmentedItemActivoTab,
  segmentedItemFicha,
  segmentedTrack,
  segmentedTrackFicha,
} from "@/components/ui/segmented"

/**
 * Variante visual del control. `segmented` es el de siempre (activo blanco);
 * `ficha` es el de nivel superior del DS §4.4bis (activo navy), para cuando
 * elegir cambia TODO el contenido de la pantalla. Ver `segmented.ts` para por
 * qué el activo va oscuro.
 */
export type TabsVariant = "segmented" | "ficha"

/** La variante se declara UNA vez, en el root, y baja por contexto. Repetirla
 * en cada `<TabsTrigger>` sería la forma más fácil de terminar con una pestaña
 * blanca entre tres navy el día que alguien agregue la cuarta. */
const TabsVariantContext = React.createContext<TabsVariant>("segmented")

/**
 * Tabs del Design System — el segmented cuando elegir cambia el PANEL visible.
 *
 * El look sale de `segmented.ts`, compartido con `ToggleGroup`. Si vas a
 * filtrar o cambiar la vista de la MISMA pantalla (sin panel asociado), no uses
 * esto: `role=tab` sin `aria-controls` miente. Usá
 * `<ToggleGroup variant="segmented">`, que se ve igual y anuncia radiogroup.
 *
 * Cada ítem puede llevar su contador en Geist Mono — usá <TabsCount>.
 */
function Tabs({
  className,
  variant = "segmented",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsVariant }) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot="tabs"
        data-variant={variant}
        className={cn("flex flex-col gap-3", className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  )
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(variant === "ficha" ? segmentedTrackFicha : segmentedTrack, className)}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        variant === "ficha"
          ? [segmentedItemFicha, segmentedItemActivoFicha]
          : [segmentedItem, segmentedItemActivoTab],
        className,
      )}
      {...props}
    />
  )
}

/** Contador del segmented: mono, discreto, y sólo con dato real. */
function TabsCount({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="tabs-count"
      className={cn(segmentedCount, className)}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsCount, TabsContent }
